import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, Activity, ShoppingBag, LayoutDashboard, Info, ArrowUpRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SiInstagram, SiYoutube, SiFacebook } from 'react-icons/si';
import { cn } from '@/lib/utils';
import { ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';
interface LayoutProps {
  children: React.ReactNode;
}
export function Layout({
  children
}: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);
  const navItems = [{
    label: '체질 진단',
    path: ROUTE_PATHS.DIAGNOSIS,
    icon: Activity
  }, {
    label: '단백질 제품',
    path: ROUTE_PATHS.PRODUCTS,
    icon: ShoppingBag
  }, {
    label: '나의 관리',
    path: ROUTE_PATHS.MANAGEMENT,
    icon: LayoutDashboard
  }, {
    label: '브랜드 스토리',
    path: ROUTE_PATHS.ABOUT,
    icon: Info
  }];
  return <div className="min-h-screen flex flex-col bg-background font-sans">
      <header className={cn('fixed top-0 w-full z-50 transition-all duration-300 h-16', isScrolled ? 'bg-background/80 backdrop-blur-md border-b border-border shadow-sm' : 'bg-transparent')}>
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2 group">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center text-primary-foreground font-bold text-xl group-hover:scale-110 transition-transform">
              K
            </div>
            <span className="text-xl font-bold tracking-tighter text-foreground font-mono">단백환</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navItems.map(item => <NavLink key={item.path} to={item.path} className={({
            isActive
          }) => cn('text-sm font-medium transition-colors hover:text-primary relative py-2 group', isActive ? 'text-primary' : 'text-muted-foreground')}>
                {item.label}
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
              </NavLink>)}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <Button variant="outline" size="sm" asChild>
              <Link to={ROUTE_PATHS.DIAGNOSIS}>지금 시작하기</Link>
            </Button>
          </div>

          <button className="md:hidden p-2 text-foreground" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu">
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {isMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="fixed inset-0 z-40 bg-background pt-20 px-4 md:hidden">
            <nav className="flex flex-col gap-6">
              {navItems.map(item => <NavLink key={item.path} to={item.path} className={({
            isActive
          }) => cn('flex items-center justify-between text-2xl font-semibold p-4 rounded-xl transition-all', isActive ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted')}>
                  <div className="flex items-center gap-4">
                    <item.icon size={28} />
                    {item.label}
                  </div>
                  <ChevronRight size={20} className="text-muted-foreground" />
                </NavLink>)}
              <Button className="mt-4 h-14 text-lg" asChild>
                <Link to={ROUTE_PATHS.DIAGNOSIS}>맞춤형 체질 진단 받기</Link>
              </Button>
            </nav>
          </motion.div>}
      </AnimatePresence>

      <main className="flex-1 pt-16">
        {children}
      </main>

      <footer className="bg-muted/30 border-t border-border">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="col-span-1 md:col-span-2">
              <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2 mb-6">
                <div className="w-6 h-6 bg-primary rounded flex items-center justify-center text-primary-foreground font-bold text-sm">
                  A
                </div>
                <span className="text-lg font-bold tracking-tighter text-foreground font-mono">단백환</span>
              </Link>
              <p className="text-muted-foreground text-sm leading-relaxed max-w-sm mb-8">
                사상체질 이론을 기반으로 당신의 고유한 생리적 특성을 분석하고,
                체질에 최적화된 한방 단백질(단백환) 관리를 통해 지속 가능한 건강함을 제안합니다.
              </p>
              <div className="flex items-center gap-4">
                <a href="#" className="p-2 rounded-full bg-background border border-border hover:text-primary transition-colors">
                  <SiInstagram size={18} />
                </a>
                <a href="#" className="p-2 rounded-full bg-background border border-border hover:text-primary transition-colors">
                  <SiYoutube size={18} />
                </a>
                <a href="#" className="p-2 rounded-full bg-background border border-border hover:text-primary transition-colors">
                  <SiFacebook size={18} />
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-6">서비스</h4>
              <ul className="space-y-4">
                {navItems.map(item => <li key={item.path}>
                    <Link to={item.path} className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
                      {item.label}
                      <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100" />
                    </Link>
                  </li>)}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-6">고객지원</h4>
              <ul className="space-y-4 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-primary transition-colors">이용약관</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">개인정보처리방침</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">B2B 한의원 파트너십</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">자주 묻는 질문</a></li>
              </ul>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-xs text-muted-foreground">
              <p>PlainWhite | 대표자: 정종혁 | 사업자등록번호: 000-00-00000</p>
              <p>서울특별시 강남구 테헤란로 123, 한방 웰니스 타워 10층</p>
            </div>
            <p className="text-xs text-muted-foreground font-mono">
              © 2026 PlainWhite. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}